import type { HomeAttributes } from "building-types/building/homeAttributes";
import { SystemParameterHomeAttributesKeys } from "building-types/building/homeAttributes";
import type { Doc } from "building-types/doc";
import { docEntityIdFromString } from "building-types/doc/docEntityId";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOptionSet } from "building-types/options/optionSet";
import {
  type SystemParameter,
  type ParameterValue,
  type Parameter,
  ParameterType,
} from "building-types/options/parameter";
import type {
  BuildingParameterValues,
  RoomParameterValues,
  ViewportParameterValues,
} from "building-types/systemParameterTypes";
import { ActiveOptionStateParameterValue } from "building-types/systemParameterTypes";
import type { Table, RWTable } from "common/data/table";
import { isNullish } from "common/utils/isNullish";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { optionSetDefaultOptionId } from "./options/optionSetBaseUtils";
import { evaluateParameterDefaultValues, parameterFormatName } from "./parameterCoreTools";
import {
  BuildingParameters,
  DateParameters,
  RoomParameters,
  SheetParameters,
  SystemParameters,
  SystemParametersFromDoc,
  ViewportParameters,
} from "./systemParameters";

export function isSystemParameterId(doc: Doc, paramId: string): boolean {
  const { systemParameters } = systemParametersFromDoc(doc);
  return !!systemParameters[paramId];
}

interface SystemParameterTables {
  readonly systemParameters: Table<SystemParameter>;
  readonly systemParameterDefaultValues: Table<ParameterValue>;
}

interface SystemParameterArgs {
  readonly optionSets: Table<DocOptionSet>;
  readonly optionChoices: OptionChoices;
  readonly attributes: HomeAttributes;
}

/**
 * Determine the system global parameters that are available to a PlanSetTemplate from the doc only (i.e. no parameters that need the building).
 */
const systemParametersFromDocWithoutBuildingCache = new LRUObjectCache<SystemParameterArgs, SystemParameterTables>(
  1000,
  (a, b) => a.optionSets === b.optionSets && a.optionChoices === b.optionChoices && a.attributes === b.attributes,
);

export function systemParametersFromDocWithoutBuilding(
  doc: Pick<Doc, "optionSets" | "optionChoices" | "attributes">,
  userProvidedOptionChoices?: OptionChoices,
): SystemParameterTables {
  const args = {
    optionSets: doc.optionSets,
    optionChoices: userProvidedOptionChoices ?? doc.optionChoices,
    attributes: doc.attributes,
  };
  const cached = systemParametersFromDocWithoutBuildingCache.get(args);
  if (cached) {
    return cached;
  }

  const result = systemParametersFromDocWithParameterList(args, SystemParametersFromDoc);
  systemParametersFromDocWithoutBuildingCache.add(args, result);
  return result;
}

/**
 * Determine the system global parameters that are available to a PlanSetTemplate.
 */
const systemParameterTablesCache = new LRUObjectCache<SystemParameterArgs, SystemParameterTables>(
  1000,
  (a, b) => a.optionSets === b.optionSets && a.optionChoices === b.optionChoices && a.attributes === b.attributes,
);

export function systemParametersFromDoc(
  doc: Pick<Doc, "optionSets" | "optionChoices" | "attributes">,
  userProvidedOptionChoices?: OptionChoices,
): SystemParameterTables {
  const args = {
    optionSets: doc.optionSets,
    optionChoices: userProvidedOptionChoices ?? doc.optionChoices,
    attributes: doc.attributes,
  };
  const cached = systemParameterTablesCache.get(args);
  if (cached) {
    return cached;
  }

  const result = systemParametersFromDocWithParameterList(args, SystemParameters);
  systemParameterTablesCache.add(args, result);
  return result;
}

function optionChoiceParameters(
  args: Pick<SystemParameterArgs, "optionSets" | "optionChoices">,
  userProvidedOptionChoices?: OptionChoices,
): RWTable<SystemParameter> {
  const optionChoices = userProvidedOptionChoices ?? args.optionChoices;
  return sortEntitiesBySortIndex(Object.values(args.optionSets)).reduce((acc, optionSet, i) => {
    const defaultValue =
      optionChoices[optionSet.id] ?? optionSetDefaultOptionId(optionSet, args.optionSets) ?? undefined;

    const param: SystemParameter = {
      id: optionSet.id,
      entityType: "SYSTEM_PARAMETER",
      type: ParameterType.OPTION,
      optionSetId: docEntityIdFromString("OPTION_SET", optionSet.id),
      sortIndex: i,
      name: "ActiveOptionState_" + parameterFormatName(optionSet.name),
      defaultValue: defaultValue ? docEntityIdFromString("OPTION", defaultValue) : undefined,
    };

    acc[optionSet.id] = param;

    return acc;
  }, {} as RWTable<SystemParameter>);
}

export function optionChoiceParameterValues(
  args: Pick<SystemParameterArgs, "optionSets" | "optionChoices">,
  userProvidedOptionChoices?: OptionChoices,
): Table<ParameterValue> {
  const params = optionChoiceParameters(args, userProvidedOptionChoices);
  return evaluateParameterDefaultValues(params);
}

function systemParametersFromDocWithParameterList(
  args: SystemParameterArgs,
  allParameters: Table<Parameter>,
): SystemParameterTables {
  const systemParameters: RWTable<SystemParameter> = {
    ...allParameters,
    // collect active option state params
    ...optionChoiceParameters(args),

    activeOptionState: {
      id: "activeOptionState",
      entityType: "SYSTEM_PARAMETER",
      type: ParameterType.PRESET,
      sortIndex: 0,
      name: "ActiveOptionState",
      defaultValue: docEntityIdFromString("PRESET", ActiveOptionStateParameterValue),
    },
  };

  // set default values for parameters from home attributes
  for (const key in SystemParameterHomeAttributesKeys) {
    if (systemParameters[key]) {
      systemParameters[key] = {
        ...systemParameters[key],
        defaultValue: args.attributes[key] ?? systemParameters[key].defaultValue,
      };
    }
  }

  return {
    systemParameters,
    systemParameterDefaultValues: evaluateParameterDefaultValues(systemParameters),
  };
}

export function isSystemParameterHomeAttributesKey(key: string): boolean {
  return SystemParameterHomeAttributesKeys[key];
}

export function isDateParameterId(paramId: string): boolean {
  return (Object.values(DateParameters) as SystemParameter[]).some((x) => x.id === paramId);
}

export function isViewportParameterId(paramId: string): paramId is keyof ViewportParameterValues {
  return (Object.values(ViewportParameters) as SystemParameter[]).some((x) => x.id === paramId);
}
export function isRoomParameterId(paramId: string): paramId is keyof RoomParameterValues {
  return (Object.values(RoomParameters) as SystemParameter[]).some((x) => x.id === paramId);
}

export function isBuildingParameterId(paramId: string): paramId is keyof BuildingParameterValues {
  return (Object.values(BuildingParameters) as SystemParameter[]).some((x) => x.id === paramId);
}

export function isSheetParameterId(paramId: string): boolean {
  return (Object.values(SheetParameters) as SystemParameter[]).some((x) => x.id === paramId);
}

export function extractSystemParameterValuesFromHomeAttributes(
  attributes: Partial<HomeAttributes>,
): Table<ParameterValue> {
  const values: RWTable<ParameterValue> = {};
  for (const key in SystemParameterHomeAttributesKeys) {
    if (isSystemParameterHomeAttributesKey(key) && !isNullish(attributes[key])) {
      values[key] = attributes[key];
    }
  }
  return values;
}
